import moment from 'moment'

/* Keys
----------------------------------------------------------------------------- */
const GLOBAL_KEYS = {
  EventsTable: 'EventsTable',
  Date: 'DATE',
  Year: 'YEAR',
  Project: 'Project'
}

const DATE_TIME_KEYS = {
  Date: 'Date'
}

const PROJECT_KEYS = {
  Name: 'Name',
  ProjectNumber: 'ProjectNumber',
  Start: 'Start',
  Stop: 'Stop',
  MembersList: 'MembersList',
  Manager: 'Manager'
}

const CONTACT_KEYS = {
  Company: 'Company',
  Phone: 'Phone',
  Email: 'Email',
  FullAddress: 'FullAddress',
  AddressLine1: 'AddressLine1',
  AddressLine2: 'AddressLine2',
  City: 'City',
  ZipCode: 'ZipCode',
  Country: 'Country',
  StateProvinceRegion: 'StateProvinceRegion'
}


const USER_KEYS = {
  FullName: 'FullName',
  Email: 'Email',
  Contact: 'Contact'
}


function getGlobalFullKeys () {
  const globalKeys = []
  const projectKeys = getProjectFullKeys()

  for (const key of Object.values(GLOBAL_KEYS)) {
    switch (key) {
      case GLOBAL_KEYS.Project:
        for (const projectKey of projectKeys) {
          globalKeys.push(`${key}.${projectKey}`)
        }
        break
      default:
        globalKeys.push(key)
        break
    }
  }

  return globalKeys
}

function getDateTimeFullKeys () {
  return Object.values(DATE_TIME_KEYS)
}

function getUserFullKeys () {
  const userKeys = []
  const contactKeys = getContactFullKeys(false)

  for (const key of Object.values(USER_KEYS)) {
    switch (key) {
      case USER_KEYS.Contact:
        for (const contactKey of contactKeys) {
          userKeys.push(`${key}.${contactKey}`)
        }
        break
      default:
        userKeys.push(key)
        break
    }
  }

  return userKeys
}

function getProjectFullKeys () {
  const projectKeys = []
  const userKeys = getUserFullKeys(false)
  const dateTimeKeys = getDateTimeFullKeys(true)

  for (const key of Object.values(PROJECT_KEYS)) {
    switch (key) {
      case PROJECT_KEYS.Manager:
        for (const userKey of userKeys) {
          projectKeys.push(`${key}.${userKey}`)
        }
        break
      case PROJECT_KEYS.Start:
      case PROJECT_KEYS.Stop:
        for (const dateTimeKey of dateTimeKeys) {
          projectKeys.push(`${key}.${dateTimeKey}`)
        }
        break
      default:
        projectKeys.push(key)
        break
    }
  }

  return projectKeys
}

function getContactFullKeys (includeMemberKeys = false) {
  const keys = Object.values(CONTACT_KEYS)
  return keys
}


/* Resolving
----------------------------------------------------------------------------- */
function resolveGlobalPlaceholder ({ project }, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case GLOBAL_KEYS.Date:
      value = moment().format('DD.MM.YYYY') // ('L')
      break
    case GLOBAL_KEYS.Year:
      value = moment().format('YYYY')
      break
    case GLOBAL_KEYS.Project:
      value = resolveProjectPlaceholder(project, placeholder, i + 1)
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveDateTimePlaceholder (dateTime, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!dateTime) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key
  const dateTimeMoment = moment(dateTime)

  switch (keyPart) {
    case DATE_TIME_KEYS.Date:
      value = dateTimeMoment.format('DD.MM.YYYY') // ('L')
      break
    case DATE_TIME_KEYS.Time:
      value = dateTimeMoment.format('HH:mm') // ('LT')
      break
    default:
      value = dateTimeMoment.format('DD.MM.YYYY HH:mm')
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveUserPlaceholder (user, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!user) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case USER_KEYS.FullName:
      value = user.fullName
      break
    case USER_KEYS.Email:
      value = user.email
      break
    case USER_KEYS.Contact:
      if (user.contact) {
        value = resolveContactPlaceholder(user.contact, placeholder, i + 1)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveProjectPlaceholder (project, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!project) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case PROJECT_KEYS.Name:
      value = project.title
      break
    case PROJECT_KEYS.ProjectNumber:
      value = project.projectNumber
      break
    case PROJECT_KEYS.Start:
      value = resolveDateTimePlaceholder(project.startUtc, placeholder, i + 1)
      break
    case PROJECT_KEYS.Stop:
      value = resolveDateTimePlaceholder(project.stopUtc, placeholder, i + 1)
      break
    case PROJECT_KEYS.MembersList:
      value = project.members.map(member => member.contact.displayName).join(', ')
      break
    case PROJECT_KEYS.MembersListOthers:
      value = project.otherMembers?.map(member => member.contact.displayName).join(', ')
      break
    case PROJECT_KEYS.Manager:
      if (project.manager) {
        value = resolveUserPlaceholder(project.manager, placeholder, i + 1)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function resolveContactPlaceholder (contact, placeholder, i = 0) {
  const { key, keyParts, defaultValue } = placeholder

  if (!contact) return valueOrDefault(null, defaultValue)

  let value = null
  const keyPart = keyParts.length > i ? keyParts[i] : key

  switch (keyPart) {
    case CONTACT_KEYS.FirstName:
      value = contact.firstName
      break
    case CONTACT_KEYS.LastName:
      value = contact.lastName
      break
    case CONTACT_KEYS.Company:
      value = contact.company
      break
    case CONTACT_KEYS.Phone:
      value = contact.phone?.phone
      break
    case CONTACT_KEYS.Email:
      value = contact.email?.email
      break
    case CONTACT_KEYS.OrganisationNumber:
      value = contact.organisationNumber
      break
    case CONTACT_KEYS.SocialSecurityNumber:
      value = contact.socialSecurityNumber
      break
    case CONTACT_KEYS.FullAddress:
      value = contact.fullAddress
      break
    case CONTACT_KEYS.AddressLine1:
      value = contact.address?.addressLine1
      break
    case CONTACT_KEYS.AddressLine2:
      value = contact.address?.addressLine2
      break
    case CONTACT_KEYS.City:
      value = contact.address?.city
      break
    case CONTACT_KEYS.Country:
      value = contact.address?.country
      break
    case CONTACT_KEYS.ZipCode:
      value = contact.address?.zipCode
      break
    case CONTACT_KEYS.StateProvinceRegion:
      value = contact.address?.stateProvinceRegion
      break
    default:
      if (contact.member) {
        value = resolveMemberPlaceholder(contact.member, placeholder, i)
      }
      break
  }

  value = valueOrDefault(value, defaultValue)

  return value
}


function valueOrDefault (value, defaultValue) {
  if (value === null || value === '' || value === undefined) {
    value = defaultValue
  }
  if (value === '' || value === undefined) {
    value = null
  }
  if (value !== null && value !== undefined) {
    value = String(value)
  }
  return value
}


/* Formatting & Parsing
----------------------------------------------------------------------------- */
/* valid placeholder examples:
  {{name:key.subkey|default}}
  {{key.subkey|default}}
  {{key}}
*/

const PLACEHOLDER_REGEX = /{{[\w+:]*\w+[.\w+]*\|*\w+}}/g


function formatPlaceholder ({ name, key, defaultValue }) {
  let placeholder = name ? `${name}:${key}` : key

  if (defaultValue) {
    placeholder += `|${defaultValue}`
  }

  return '{{' + placeholder + '}}'
}


function parsePlaceholder (placeholder) {
  let p = placeholder.replace(/[{}]/g, '')

  const nameSplit = p.split(':')
  let name = null
  if (nameSplit.length > 1) {
    name = nameSplit[0]
    p = nameSplit[1]
  }

  const defaultValueSplit = p.split('|')
  let defaultValue = ''
  if (defaultValueSplit.length > 1) {
    defaultValue = defaultValueSplit[1]
    p = defaultValueSplit[0]
  }

  const key = p
  const keySplit = p.split('.')

  const placeholderObj = {
    expression: placeholder,
    name: name,
    key: key,
    keyParts: keySplit,
    defaultValue: defaultValue
  }

  return placeholderObj
}

const recursiveSearch = (obj, searchKey, results = []) => {
  const res = results
  Object.keys(obj).forEach(key => {
    const value = obj[key]
    if (key === searchKey && typeof value !== 'object') {
      res.push(value)
    } else if (typeof value === 'object') {
      recursiveSearch(value, searchKey, res)
    }
  })
  return res
}


function parsePlaceholdersFromSfdt (sfdt) {
  // sfdt is JSON where we need to concat only the "text" prop values to get raw text without formatting

  const results = []
  recursiveSearch(JSON.parse(sfdt), 'text', results)
  const text = results.join('')

  const placeholders = []
  const matches = text.match(PLACEHOLDER_REGEX)

  if (matches) {
    for (const match of matches) {
      const placeholder = parsePlaceholder(match)
      placeholders.push(placeholder)
    }
  }

  return placeholders
}


function containsKeyStartingWith (placeholders, keyStart) {
  return placeholders.find(x => x.key.startsWith(keyStart)) !== undefined
}



export default {
  GLOBAL_KEYS,
  DATE_TIME_KEYS,
  PROJECT_KEYS,

  formatPlaceholder,
  parsePlaceholder,
  parsePlaceholdersFromSfdt,
  containsKeyStartingWith,

  getGlobalFullKeys,

  resolveGlobalPlaceholder,
  resolveContactPlaceholder
}
